import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFirestore, doc, getDoc, getDocs, setDoc, addDoc, collection, onSnapshot, query, where, runTransaction } from "firebase/firestore";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import app, { db } from "../../../backend/firebaseConfig";
import { addNotification } from "../../../components/functions/notifyComp";

// Imports MUI Components
import {  Box,
          Button,
          Stack,
          Typography,
          Grid,
          CardContent,
          Dialog,
          DialogTitle,
          DialogContent,
          DialogActions,
          IconButton,
          CircularProgress,
          Snackbar,
          Alert,
          Avatar,
          Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails 
} from "@mui/material"
import { Close, Visibility, VisibilityOff } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

// Import Organisms
import Sidebar from "../../../components/UI/organisms/sidebar/Sidebar"
import Header from "../../../components/UI/organisms/header/logged-in/HeaderLoggedIn"
import ButtonComp from "../../../components/UI/atoms/button/Button";
import SplashScreen from "../../../components/UI/atoms/splash/SplashScreen"

// Import Functions
import { fetchUserDetails } from "../../../components/functions/fetchUserDetails";
import { notifyFirstUser } from "../../../components/functions/notifyFirstUser";
import { redirectIfNotLoggedIn } from "../../../components/functions/redirectIfNotLoggedIn";

import Text from "../../../components/UI/atoms/text/Text";
import Icon from "../../../components/UI/atoms/icon/Icon";
import Search from "../../../components/UI/molecules/search/Search";
import Fab from "../../../components/UI/atoms/fab/Fab";

const ManagePersonnel = () => {
    const [clinicStaff, setClinicStaff] = useState([]);
    const [dentistOwner, setDentistOwner] = useState([]);
    const [associateDentist, setAssociateDentist] = useState([]);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [filter, setFilter] = useState("all");
    const [patientCounts, setPatientCounts] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [editingPerson, setEditingPerson] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalData, setSuccessModalData] = useState({});
    const [newStaffType, setNewStaffType] = useState("ClinicStaff");
    const [newStaffEmail, setNewStaffEmail] = useState("");
    const [newStaffPassword, setNewStaffPassword] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showNewStaffPassword, setShowNewStaffPassword] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteModalData, setDeleteModalData] = useState({});
    const [isRemovingPersonnel, setIsRemovingPersonnel] = useState(false);
    const [isCancellingDelete, setIsCancellingDelete] = useState(false);
    const [showRemovalSnackbar, setShowRemovalSnackbar] = useState(false);
    const [removalSnackbarMessage, setRemovalSnackbarMessage] = useState("");
    // New state variables for email validation
    const [emailError, setEmailError] = useState("");
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    // New state variables for form validation
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [emailValidationError, setEmailValidationError] = useState("");
    const [passwordValidationError, setPasswordValidationError] = useState("");
    // Snackbar state for specialty validation
    const [showSpecialtySnackbar, setShowSpecialtySnackbar] = useState(false);
    const [specialtySnackbarMessage, setSpecialtySnackbarMessage] = useState("");
    const [specialtySnackbarSeverity, setSpecialtySnackbarSeverity] = useState("error");

  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("")
  const [alertImage, setAlertImage] = useState("");
  const [alertButton, setAlertButton] = useState("")
  const [alertButtonType, setAlertButtonType] = useState("false")
  const [alertLink, setAlertLink] = useState("")
  const [isRemovePersonnel, setIsRemovePersonnel] = useState(false)

 // Initialization states
  const [expanded, setExpanded] = useState(false)


  // Function to handle accordion expansion
  const handleChange = (isExpanded, panel) => {
    setExpanded(isExpanded ? panel : false)
  }

    // Store unsubscribe functions for manual cleanup
    const [unsubscribeFunctions, setUnsubscribeFunctions] = useState([]);

    const navigate = useNavigate();
    const auth = getAuth(app);

  const PERIOD_OPTIONS = [
    { key: "all", label: "All Personnel" },
    { key: "clinicstaff", label: "Clinic Staff" },
    { key: "associatedentist", label: "Associate Dentists" },
    { key: "dentistowner", label: "Dentist Owners" },
  ];

  // UseEffects
  useEffect(() => {
    const details = fetchUserDetails(setUserDetails);
    const redirect = redirectIfNotLoggedIn(setShowCustomAlert);
    return () => {
      details && details();
      redirect && redirect();
    };
  }, []);

  // Prevent Esc key from closing the success modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showSuccessModal && e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (showSuccessModal) {
      window.addEventListener("keydown", handleKeyDown, true);
      return () => {
        window.removeEventListener("keydown", handleKeyDown, true);
      };
    }
  }, [showSuccessModal]);

    // Helper function to check if email/username already exists in Firestore
    const checkEmailExists = async (email) => {
        if (!email || email.trim() === "") {
            setEmailError("");
            return false;
        }

        setIsCheckingEmail(true);
        setEmailError("");

        try {
            const db = getFirestore(app);
            
            // Check in users collection
            const usersRef = collection(db, "users");
            const usersQuery = query(usersRef, where("email", "==", email.trim()));
            const usersSnapshot = await getDocs(usersQuery);
            
            if (!usersSnapshot.empty) {
                setEmailError("This email is already associated with an existing user account.");
                setIsCheckingEmail(false);
                return true;
            }

            // Check in pendingPersonnel collection
            const pendingRef = collection(db, "pendingPersonnel");
            const pendingQuery = query(pendingRef, where("email", "==", email.trim()));
            const pendingSnapshot = await getDocs(pendingQuery);
            
            if (!pendingSnapshot.empty) {
                setEmailError("This email/username is already in the pending personnel list.");
                setIsCheckingEmail(false);
                return true;
            }

            // Check in patients collection (if patients use email as identifier)
            const patientsRef = collection(db, "patients");
            const patientsQuery = query(patientsRef, where("email", "==", email.trim()));
            const patientsSnapshot = await getDocs(patientsQuery);
            
            if (!patientsSnapshot.empty) {
                setEmailError("This email is already associated with a patient account.");
                setIsCheckingEmail(false);
                return true;
            }

            setEmailError("");
            setIsCheckingEmail(false);
            return false;
        } catch (error) {
            console.error("Error checking email existence:", error);
            setEmailError("Error checking email availability. Please try again.");
            setIsCheckingEmail(false);
            return false;
        }
    };

    // Handle email input change with validation
    const handleEmailChange = async (e) => {
        const email = e.target.value;
        setNewStaffEmail(email);
        
        // Clear previous error when user starts typing
        if (emailError) {
            setEmailError("");
        }
        
        // Clear validation errors when user starts typing
        if (showValidationErrors && emailValidationError) {
            setEmailValidationError("");
        }
        
        // Check email existence after user stops typing (debounced)
        if (email.trim()) {
            // Clear any existing timeout
            if (window.emailCheckTimeout) {
                clearTimeout(window.emailCheckTimeout);
            }
            
            // Set new timeout for 500ms delay
            window.emailCheckTimeout = setTimeout(() => {
                checkEmailExists(email);
            }, 500);
        }
    };

    // listen for authentication state changes to get the current user ID
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(user => {
            if (user) {
                setCurrentUserId(user.uid);
            }
        });

        return () => {
            unsubscribe();
            // Clean up email check timeout when component unmounts
            if (window.emailCheckTimeout) {
                clearTimeout(window.emailCheckTimeout);
            }
        };
    }, [auth]);

    // fetch personnel data from Firestore
    useEffect(() => {
        const fetchPersonnel = async () => {
            // Fetch all users and filter in JS so only accountDeleted: true is excluded
            const usersRef = collection(db, "users");
            try {
                const snapshot = await getDocs(usersRef);

                const clinicStaffData = [];
                const dentistOwnerData = [];
                const associateDentistData = [];

                snapshot.forEach((doc) => {
                    const data = doc.data();
                    // Exclude users with accountDeleted: true (boolean or string) or accountStatus === 'deleted' (case-insensitive)
                    const isDeleted =
                        data.accountDeleted === true ||
                        data.accountDeleted === "true" ||
                        (typeof data.accountStatus === "string" && data.accountStatus.toLowerCase() === "deleted");
                    if (isDeleted) return;
                    const userData = { id: doc.id, ...data };

                    if (data.userType === "ClinicStaff" || data.role === "ClinicStaff") {
                        clinicStaffData.push(userData);
                    } else if (data.userType === "DentistOwner" || data.role === "DentistOwner") {
                        dentistOwnerData.push(userData);
                    } else if (data.userType === "AssociateDentist" || data.role === "AssociateDentist") {
                        associateDentistData.push(userData);
                    }
                });

                setClinicStaff(clinicStaffData);
                setDentistOwner(dentistOwnerData);
                setAssociateDentist(associateDentistData);
            } catch (error) {
                console.error("Error fetching personnel:", error);
            }
        };

        fetchPersonnel();
    }, []);

    // Fetch user details for sidebar profile (search all personnel types)
    useEffect(() => {
        const personnelTypes = ["DentistOwner", "AssociateDentist", "ClinicStaff"];
        const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                for (const type of personnelTypes) {
                    const userRef = doc(db, "users", user.uid);
                    const snapshot = await getDoc(userRef);
                    if (snapshot.exists()) {
                        setUserDetails(snapshot.data());
                        break;
                    }
                }
            }
        });
        
        // Store unsubscribe function for manual cleanup
        setUnsubscribeFunctions(prev => [...prev, authUnsubscribe]);
        
        return () => {
            authUnsubscribe();
        };
    }, [auth]);

    // Fetch all appointments and count unique patients per dentist
    useEffect(() => {
        const appointmentsRef = collection(db, "appointments");
        const unsubscribe = onSnapshot(appointmentsRef, (snapshot) => {
            // Use dentist ID as the key (not email) to handle dentists without email
            const dentistPatientCounts = {};
            const allPersonnel = [...dentistOwner, ...associateDentist, ...clinicStaff];
            
            // Create a map of dentist identifiers to their unique key (id or uid)
            // This maps dentistId, uid, id, and name variations to the dentist's unique key
            const dentistKeyMap = {};
            
            allPersonnel.forEach(person => {
                if ((person.userType === "DentistOwner" || person.role === "DentistOwner" || 
                     person.userType === "AssociateDentist" || person.role === "AssociateDentist")) {
                    
                    // Use person.id (Firestore document ID) as the unique key for this dentist
                    const dentistKey = person.id;
                    if (!dentistKey) return;
                    
                    // Map by Firestore document ID
                    dentistKeyMap[person.id] = dentistKey;
                    
                    // Map by Firebase Auth UID (this is what appointments use in dentistId field)
                    if (person.uid) {
                        dentistKeyMap[person.uid] = dentistKey;
                    }
                    
                    // Build name variations
                    const firstName = (person.firstName || '').trim();
                    const middleName = (person.middleName || '').trim();
                    const lastName = (person.lastName || '').trim();
                    
                    // Map by display name formats (without middle name)
                    if (firstName && lastName) {
                        dentistKeyMap[`Dr. ${firstName} ${lastName}`] = dentistKey;
                        dentistKeyMap[`${firstName} ${lastName}`] = dentistKey;
                    }
                    
                    // Map by display name formats (with middle name)
                    if (firstName && middleName && lastName) {
                        dentistKeyMap[`Dr. ${firstName} ${middleName} ${lastName}`] = dentistKey;
                        dentistKeyMap[`${firstName} ${middleName} ${lastName}`] = dentistKey;
                    }
                }
            });
            
            // Initialize patient sets for each dentist using their unique key
            allPersonnel.forEach(person => {
                if ((person.userType === "DentistOwner" || person.role === "DentistOwner" || 
                     person.userType === "AssociateDentist" || person.role === "AssociateDentist") && person.id) {
                    dentistPatientCounts[person.id] = new Set();
                }
            });
            
            // Iterate through each date document
            snapshot.forEach((dateDoc) => {
                const dateData = dateDoc.data();
                
                // Iterate through each appointment within the date
                Object.entries(dateData).forEach(([appointmentId, appt]) => {
                    // Only count completed appointments
                    if (appt.status === "Completed" || appt.status === "completed") {
                        // Find dentist key by dentistId (UID) or dentist (display name)
                        let dentistKey = null;
                        
                        // Try to match by dentistId first (Firebase Auth UID)
                        if (appt.dentistId && dentistKeyMap[appt.dentistId]) {
                            dentistKey = dentistKeyMap[appt.dentistId];
                        } 
                        // Then try by dentist name (exact match)
                        else if (appt.dentist && dentistKeyMap[appt.dentist]) {
                            dentistKey = dentistKeyMap[appt.dentist];
                        }
                        // Try case-insensitive name match as fallback
                        else if (appt.dentist) {
                            const normalizedName = appt.dentist.trim().toLowerCase();
                            for (const [key, dKey] of Object.entries(dentistKeyMap)) {
                                if (key && typeof key === 'string' && key.trim().toLowerCase() === normalizedName) {
                                    dentistKey = dKey;
                                    break;
                                }
                            }
                        }
                        
                        if (dentistKey) {
                            // Use patientId as the PRIMARY unique identifier (always present in appointments)
                            // This ensures we count unique patients, not unique emails/phones
                            const uniquePatientId = appt.patientId || appt.uid || appt.patientEmail || appt.email;
                            if (uniquePatientId) {
                                if (!dentistPatientCounts[dentistKey]) {
                                    dentistPatientCounts[dentistKey] = new Set();
                                }
                                dentistPatientCounts[dentistKey].add(uniquePatientId);
                            }
                        }
                    }
                });
            });
            
            // Convert sets to counts - map by dentist ID (person.id)
            const finalCounts = {};
            Object.entries(dentistPatientCounts).forEach(([dentistId, patients]) => {
                finalCounts[dentistId] = patients.size;
            });
            
            setPatientCounts(finalCounts);
        }, (error) => {
            // Silently handle permission errors during sign-out
            if (error.code === 'permission-denied') {
                console.log('Appointments listener stopped due to sign-out');
                return;
            }
            console.error('Error fetching appointments:', error);
        });
        
        // Store unsubscribe function for manual cleanup
        setUnsubscribeFunctions(prev => [...prev, unsubscribe]);
        
        return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dentistOwner, associateDentist, clinicStaff]);

    // Filtering logic
    let filteredClinicStaff = clinicStaff;
    let filteredDentistOwner = dentistOwner;
    let filteredAssociateDentist = associateDentist;
    if (filter === "clinicstaff") {
        filteredDentistOwner = [];
        filteredAssociateDentist = [];
    } else if (filter === "associatedentist") {
        filteredClinicStaff = [];
        filteredDentistOwner = [];
    } else if (filter === "dentistowner") {
        filteredClinicStaff = [];
        filteredAssociateDentist = [];
    }

    // Search filter logic
    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const searchLower = searchTerm.toLowerCase().trim();
    // Check if search term contains "Dr." - if so, only search dentists
    const searchHasDr = /^dr\.?\s+/i.test(searchLower);
    
    const filterPersonnel = (list, type) =>
        list.filter(person => {
            // Determine if person is a dentist (check both type parameter and person data)
            const isDentist = type === 'DentistOwner' || type === 'AssociateDentist' ||
                            person.userType === 'DentistOwner' || person.userType === 'AssociateDentist' ||
                            person.role === 'DentistOwner' || person.role === 'AssociateDentist';
            
            // If search has "Dr." prefix, exclude non-dentists (Clinic Staff)
            if (searchHasDr && !isDentist) {
                return false;
            }
            
            // Normalize name parts
            const firstName = (person.firstName || '').toLowerCase().trim();
            const middleName = (person.middleName || '').toLowerCase().trim();
            const lastName = (person.lastName || '').toLowerCase().trim();
            
            // Create full name with single spaces
            const fullName = `${firstName} ${middleName} ${lastName}`.trim().replace(/\s+/g, ' ');
            
            // For dentists, create name with "Dr." prefix
            const fullNameWithDr = isDentist ? `dr. ${fullName}` : '';
            
            // Normalize other fields
            const email = (person.email || '').toLowerCase();
            const specialty = (person.specialty || person.position || '').toLowerCase();
            
            // Remove "dr." or "dr" from search term for name matching
            const searchWithoutDr = searchLower.replace(/^dr\.?\s*/i, '').trim();
            
            // Check if search term matches individual fields
            const matchesIndividualFields = 
                firstName.includes(searchLower) ||
                middleName.includes(searchLower) ||
                lastName.includes(searchLower) ||
                email.includes(searchLower) ||
                specialty.includes(searchLower);
            
            // Check if search term matches full name
            const matchesFullName = fullName.includes(searchLower);
            
            // Check if search term (without "dr.") matches full name
            const matchesFullNameWithoutDr = searchWithoutDr && fullName.includes(searchWithoutDr);
            
            // Check if search term matches full name with "Dr." prefix (for dentists only)
            const matchesFullNameWithDr = isDentist && (
                fullNameWithDr.includes(searchLower) ||
                (searchLower.includes('dr') && searchWithoutDr && fullName.includes(searchWithoutDr))
            );
            
            // Split search term into words (handle "Dr." prefix properly)
            const searchWords = searchLower.split(/\s+/).filter(word => {
                const normalizedWord = word.replace(/^dr\.?$/i, '').trim();
                return normalizedWord.length > 0;
            });
            
            // Check if all search words match any part of the name
            const matchesAllWords = searchWords.length > 0 && searchWords.every(word => {
                const normalizedWord = word.replace(/^dr\.?$/i, '').trim();
                if (!normalizedWord) return true; // If word is just "dr" or "dr.", skip it
                return firstName.includes(normalizedWord) ||
                       middleName.includes(normalizedWord) ||
                       lastName.includes(normalizedWord) ||
                       fullName.includes(normalizedWord);
            });
            
            return matchesIndividualFields || matchesFullName || matchesFullNameWithoutDr || matchesFullNameWithDr || matchesAllWords;
        });

    // Filter out users with accountDeleted: true before rendering
    filteredClinicStaff = filterPersonnel(filteredClinicStaff.filter(person => !person.accountDeleted), 'ClinicStaff');
    filteredDentistOwner = filterPersonnel(filteredDentistOwner.filter(person => !person.accountDeleted), 'DentistOwner');
    filteredAssociateDentist = filterPersonnel(filteredAssociateDentist.filter(person => !person.accountDeleted), 'AssociateDentist');

    // function to handle removal of personnel accounts
    const handleDelete = ({ userType, id, firstName, lastName, gender }) => {
        const fullName = `${firstName || ""} ${lastName || ""}`.replace(/\s+/g, " ").trim();
        
        setDeleteModalData({
            userType,
            id,
            firstName,
            lastName,
            gender,
            personName: fullName
        });
        setIsRemovingPersonnel(false);
        setIsCancellingDelete(false);
        setShowDeleteModal(true);
    };

    // function to confirm removal
    const confirmDelete = async () => {
        if (isRemovingPersonnel) {
            return;
        }

        const { userType, id, personName } = deleteModalData;
        
        if (!id || !userType) {
            setShowDeleteModal(false);
            setDeleteModalData({});
            return;
        }

        const db = getFirestore(app);
        const userRef = doc(db, "users", id);

        setIsRemovingPersonnel(true);

        try {
            // Get the person's full details for the notification message
            const personDoc = await getDoc(userRef);
            const personData = personDoc.exists() ? personDoc.data() : null;

            // This calls your trusted backend endpoint which should use the Admin SDK.
            try {
                const apiUrl = process.env.REACT_APP_API_URL || '';
                const resp = await fetch(`${apiUrl}/api/delete-user?uid=${id}`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                });

                if (!resp.ok) {
                    // Backend failed — offer admin a Firestore-only fallback
                    const details = await resp.text().catch(() => resp.statusText || "Unknown error");
                    const proceed = window.confirm(
                        `Failed to remove Authentication account (status ${resp.status}).\n\nDetails: ${details}\n\n` +
                        `Do you want to remove the Firestore user record anyway? (Authentication user will remain)`
                    );
                    if (!proceed) {
                        setShowDeleteModal(false);
                        setDeleteModalData({});
                        setIsRemovingPersonnel(false);
                        return;
                    }
                }
            } catch (authErr) {
                // Network / fetch failure — offer admin a Firestore-only fallback
                const proceed = window.confirm(
                    `Failed to contact authentication-deletion service:\n\n${authErr?.message || authErr}\n\n` +
                    `Do you want to remove the Firestore user record anyway? (Authentication user will remain)`
                );
                if (!proceed) {
                    setShowDeleteModal(false);
                    setDeleteModalData({});
                    setIsRemovingPersonnel(false);
                    return;
                }
            }

            // The API endpoint now handles all deletion logic including:
            // - Deleting from Firebase Authentication
            // - Deleting from Firestore
            // - Canceling appointments (if DentistOwner or AssociateDentist)
            // - Sending notifications
            
            // If removing an associate dentist, we still need to notify patients locally
            // since the API only handles DentistOwner notifications
            if (userType === "AssociateDentist" && personData) {
                const appointmentsRef = collection(db, "appointments");
                const appointmentsSnapshot = await getDocs(appointmentsRef);
                
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const todayStr = today.toISOString().split('T')[0];
                
                // Format dentist name for matching appointments
                const dentistDisplayName = `Dr. ${personData.firstName} ${personData.lastName}`;
                
                // Track appointments that were cancelled (for notifications only)
                const appointmentsToNotify = {};
                
                appointmentsSnapshot.forEach((dateDoc) => {
                    const dateStr = dateDoc.id;
                    
                    // Only check current and future dates
                    if (dateStr >= todayStr) {
                        const dateData = dateDoc.data();
                        
                        Object.entries(dateData).forEach(([appointmentId, appointment]) => {
                            // Check if this appointment belongs to the dentist being removed
                            // and is pending or confirmed
                            if (appointment && 
                                appointment.dentist === dentistDisplayName &&
                                (appointment.status === "Pending" || appointment.status === "Confirmed" ||
                                 appointment.status === "pending" || appointment.status === "confirmed")) {
                                if (!appointmentsToNotify[dateStr]) {
                                    appointmentsToNotify[dateStr] = [];
                                }
                                appointmentsToNotify[dateStr].push({
                                    id: appointmentId,
                                    ...appointment
                                });
                            }
                        });
                    }
                });
                
                // Helper function to format date to "November 02, 2025"
                const formatDateLong = (dateStr) => {
                    if (!dateStr) return "";
                    const [year, month, day] = dateStr.split("-");
                    const date = new Date(year, month - 1, day);
                    const monthNames = ["January", "February", "March", "April", "May", "June",
                        "July", "August", "September", "October", "November", "December"];
                    return `${monthNames[date.getMonth()]} ${day}, ${year}`;
                };
                
                // Helper function to format time consistently
                const formatTimeConsistent = (timeStr) => {
                    if (!timeStr) return "";
                    // Handle various time formats
                    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
                    if (match) {
                        let hours = parseInt(match[1]);
                        const minutes = match[2];
                        let period = match[3] ? match[3].toUpperCase() : "";
                        
                        // If no period specified, determine from hours
                        if (!period) {
                            period = hours >= 12 ? "PM" : "AM";
                            if (hours > 12) hours -= 12;
                            if (hours === 0) hours = 12;
                        }
                        
                        return `${hours}:${minutes} ${period}`;
                    }
                    return timeStr;
                };
                
                // Send notifications to patients
                // Note: Appointments are already deleted by the API
                for (const [dateStr, appointments] of Object.entries(appointmentsToNotify)) {
                    const formattedDateLong = formatDateLong(dateStr);
                    
                    // Send notifications to patients
                    for (const appointment of appointments) {
                        const patientUid = appointment.uid || appointment.userId || appointment.patientId;
                        if (patientUid) {
                            const startTime = appointment.startTime ? formatTimeConsistent(appointment.startTime) : 
                                             (appointment.time ? formatTimeConsistent(appointment.time) : "N/A");
                            const endTime = appointment.endTime ? formatTimeConsistent(appointment.endTime) : "N/A";
                            
                            const message = `Your appointment on ${formattedDateLong}, from ${startTime} to ${endTime} has been cancelled, as the dentist is no longer with the clinic.`;
                            await addNotification(patientUid, message);
                        }
                    }
                }
            }

            // Note: User document is already deleted by the API
            // await deleteDoc(userRef); // REMOVED - API handles this now

            // Send notifications to all DentistOwners
            if (userDetails && personData) {
                const usersRef = collection(db, "users");
                const usersSnapshot = await getDocs(usersRef);
                
                // Format the remover's name (should be a DentistOwner)
                const removerName = `Dr. ${userDetails.firstName} ${userDetails.lastName}`;
                
                // Format the removed person's name and title
                let removedPersonNameWithTitle = "";
                if (userType === "AssociateDentist") {
                    removedPersonNameWithTitle = `Associate Dentist Dr. ${personData.firstName} ${personData.lastName}`;
                } else if (userType === "ClinicStaff") {
                    const fullName = `${personData.firstName} ${personData.lastName}`;
                    const gender = personData.gender || "";
                    
                    if (gender.toLowerCase() === "male") {
                        removedPersonNameWithTitle = `Mr. ${fullName} (Clinic Staff)`;
                    } else if (gender.toLowerCase() === "female") {
                        removedPersonNameWithTitle = `Ms. ${fullName} (Clinic Staff)`;
                    } else {
                        removedPersonNameWithTitle = `Mr./Ms. ${fullName} (Clinic Staff)`;
                    }
                }
                
                // Notify all DentistOwners
                if (removedPersonNameWithTitle) {
                    const notificationMessage = `${removedPersonNameWithTitle} has been removed by ${removerName}.`;
                    
                    usersSnapshot.forEach((docu) => {
                        const data = docu.data();
                        if (data.userType === "DentistOwner" || data.role === "DentistOwner") {
                            addNotification(docu.id, notificationMessage);
                        }
                    });
                }
            }

            const successMessage = getRemovalSuccessMessage(
                userType,
                {
                    ...personData,
                    personName,
                    firstName: personData?.firstName || deleteModalData.firstName,
                    lastName: personData?.lastName || deleteModalData.lastName,
                    gender: personData?.gender || deleteModalData.gender
                }
            );
            setRemovalSnackbarMessage(successMessage);
            setShowRemovalSnackbar(true);

            if (userType === "ClinicStaff") {
                setClinicStaff(clinicStaff.filter(person => person.id !== id));
            } else if (userType === "DentistOwner") {
                setDentistOwner(dentistOwner.filter(person => person.id !== id));
            } else if (userType === "AssociateDentist") {
                setAssociateDentist(associateDentist.filter(person => person.id !== id));
            }
        } catch (error) {
            console.error("Error removing personnel:", error);
            alert("Failed to remove personnel.");
        } finally {
            setIsRemovingPersonnel(false);
        }
        
        // Close modal
        setShowDeleteModal(false);
        setDeleteModalData({});
    };

    // function to cancel removal
    const cancelDelete = async () => {
        if (isCancellingDelete || isRemovingPersonnel) {
            return;
        }

        setIsCancellingDelete(true);

        await new Promise(resolve => setTimeout(resolve, 300));
        
        setShowDeleteModal(false);
        setDeleteModalData({});
        setIsCancellingDelete(false);
    };

    const getRemovalTitle = (userType) => {
        if (userType === "AssociateDentist") {
            return "Remove Associate Dentist";
        }
        if (userType === "ClinicStaff") {
            return "Remove Clinic Staff";
        }
        if (userType === "DentistOwner") {
            return "Remove Dentist Owner";
        }
        return "Remove Personnel";
    };

    const getRemovalMessage = (data) => {
        if (!data || (!data.firstName && !data.lastName && !data.personName)) {
            return "Are you sure you want to remove this personnel?";
        }

        const fullName = `${data.firstName || ""} ${data.lastName || ""}`.replace(/\s+/g, " ").trim();
        const fallbackName = data.personName || fullName;

        if (data.userType === "AssociateDentist") {
            const dentistName = fallbackName || "this associate dentist";
            return `Are you sure you want to remove Dr. ${dentistName}?`;
        }

        if (data.userType === "ClinicStaff") {
            const gender = (data.gender || "").toLowerCase();
            let honorific = "Mr./Ms.";
            if (gender === "male") {
                honorific = "Mr.";
            } else if (gender === "female") {
                honorific = "Ms.";
            }

            if (fallbackName) {
                return `Are you sure you want to remove ${honorific} ${fallbackName}?`;
            }
            return `Are you sure you want to remove ${honorific} this clinic staff member?`;
        }

        if (fallbackName) {
            return `Are you sure you want to remove ${fallbackName}?`;
        }

        return "Are you sure you want to remove this personnel?";
    };

    const getRemovalSuccessMessage = (userType, data) => {
        const fullName = `${(data?.firstName || "").trim()} ${(data?.lastName || "").trim()}`.replace(/\s+/g, " ").trim();

        if (userType === "AssociateDentist") {
            const dentistName = fullName || data?.personName || "this associate dentist";
            return `An associate dentist Dr. ${dentistName} has been successfully removed.`;
        }

        if (userType === "ClinicStaff") {
            const gender = (data?.gender || "").toLowerCase();
            let honorific = "Mr./Ms.";
            if (gender === "male") {
                honorific = "Mr.";
            } else if (gender === "female") {
                honorific = "Ms.";
            }
            const staffName = fullName || data?.personName || "this clinic staff member";
            return `A clinic staff ${honorific} ${staffName} has been successfully removed.`;
        }

        if (userType === "DentistOwner") {
            const name = fullName || data?.personName || "this dentist owner";
            return `A dentist owner ${name} has been successfully removed.`;
        }

        const name = fullName || data?.personName || "this personnel";
        return `${name} has been successfully removed.`;
    };

    const removalTitle = getRemovalTitle(deleteModalData.userType);
    const removalMessage = getRemovalMessage(deleteModalData);
    const handleRemovalSnackbarClose = (_, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setShowRemovalSnackbar(false);
        setRemovalSnackbarMessage("");
    };

    // handle logout
    const handleLogout = () => {
        // Manually unsubscribe from all listeners first
        unsubscribeFunctions.forEach(unsubscribe => {
            if (unsubscribe && typeof unsubscribe === 'function') {
                try {
                    unsubscribe();
                } catch (error) {
                    // Silently handle unsubscribe errors
                    console.log('Listener cleanup completed');
                }
            }
        });
        
        // Clear state before signing out to prevent permission errors
        setClinicStaff([]);
        setDentistOwner([]);
        setAssociateDentist([]);
        setUserDetails(null);
        setPatientCounts({});
        
        // Add a small delay to allow listeners to clean up properly
        setTimeout(() => {
            signOut(auth).then(() => {
                navigate("/", { replace: true });
            }).catch((error) => {
                // Silently handle network errors during sign-out
                if (error.code === 'auth/network-request-failed' || 
                    error.message?.includes('400') || 
                    error.message?.includes('Bad Request')) {
                    console.log('Sign out completed with network cleanup');
                } else {
                    console.error('Error during sign out:', error);
                }
                // Still navigate even if sign out fails
                navigate("/", { replace: true });
            });
        }, 100); // Small delay to allow cleanup
    };

    // function to handle editing specialty/position
    const handleEditSpecialty = async (userType, id, currentValue) => {
        setEditingPerson({ userType, id });
        setEditValue(currentValue || "");
    };

    // function to save specialty/position changes
    const handleSaveSpecialty = async () => {
        if (!editingPerson || !editValue.trim()) {
            setSpecialtySnackbarMessage("Please enter a specialty/position.");
            setSpecialtySnackbarSeverity("error");
            setShowSpecialtySnackbar(true);
            return;
        }

        const db = getFirestore(app);

        try {
            // Update the specialty/position field
            const updateData = {};
            if (editingPerson.userType === "ClinicStaff") {
                updateData.position = editValue.trim();
            } else {
                updateData.specialty = editValue.trim();
            }

            await setDoc(doc(db, "users", editingPerson.id), {
                ...(editingPerson.userType === "ClinicStaff" ? clinicStaff.find(p => p.id === editingPerson.id) : 
                    editingPerson.userType === "DentistOwner" ? dentistOwner.find(p => p.id === editingPerson.id) :
                    associateDentist.find(p => p.id === editingPerson.id)),
                ...updateData
            }, { merge: true });

            // Update local state
            if (editingPerson.userType === "ClinicStaff") {
                setClinicStaff(clinicStaff.map(person => 
                    person.id === editingPerson.id ? { ...person, position: editValue.trim() } : person
                ));
            } else if (editingPerson.userType === "DentistOwner") {
                setDentistOwner(dentistOwner.map(person => 
                    person.id === editingPerson.id ? { ...person, specialty: editValue.trim() } : person
                ));
            } else if (editingPerson.userType === "AssociateDentist") {
                setAssociateDentist(associateDentist.map(person => 
                    person.id === editingPerson.id ? { ...person, specialty: editValue.trim() } : person
                ));
            }

            // Show success snackbar
            setSpecialtySnackbarMessage("Specialty/Position Saved");
            setSpecialtySnackbarSeverity("success");
            setShowSpecialtySnackbar(true);
            setEditingPerson(null);
            setEditValue("");
        } catch (error) {
            console.error("Error updating specialty/position:", error);
            setSpecialtySnackbarMessage("Failed to update specialty/position.");
            setSpecialtySnackbarSeverity("error");
            setShowSpecialtySnackbar(true);
        }
    };

    // function to cancel editing
    const handleCancelEdit = () => {
        setEditingPerson(null);
        setEditValue("");
    };

    // Handle specialty snackbar close
    const handleSpecialtySnackbarClose = (_, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setShowSpecialtySnackbar(false);
        setSpecialtySnackbarMessage("");
    };

    // Handle modal close
    const handleModalClose = () => {
        setShowAddModal(false);
        setEmailError(""); // Clear email error when closing modal
        setNewStaffEmail("");
        setNewStaffPassword("");
        setNewStaffType("ClinicStaff");
        setShowValidationErrors(false); // Reset validation state
        setEmailValidationError(""); // Clear validation errors
        setPasswordValidationError(""); // Clear validation errors
    };

    // Add Staff/Personnel modal submit handler
    const handleAddPersonnel = async (e) => {
        e.preventDefault();
        
        // Show validation errors and check if fields are empty
        setShowValidationErrors(true);
        let hasValidationErrors = false;
        
        // Validate email field
        if (!newStaffEmail || !newStaffEmail.trim()) {
            setEmailValidationError("Email or Username is required");
            hasValidationErrors = true;
        } else {
            setEmailValidationError("");
        }
        
        // Validate password field
        if (!newStaffPassword || !newStaffPassword.trim()) {
            setPasswordValidationError("Password is required");
            hasValidationErrors = true;
        } else {
            setPasswordValidationError("");
        }
        
        if (hasValidationErrors) {
            return; // Don't proceed, let the UI show the validation errors
        }
        
        // Check if there's an email error before proceeding
        // The inline validation message already shows the error, no need for alert
        if (emailError) {
            return;
        }
        
        // Final check to ensure email doesn't exist
        // This will set the emailError state which displays inline, no alert needed
        const emailExists = await checkEmailExists(newStaffEmail);
        if (emailExists) {
            return; // The checkEmailExists function already sets emailError which displays inline
        }
        
        setIsSubmitting(true);
        try {
            const db = getFirestore(app);
            // Format date as MM/DD/YYYY HH:mm
            const now = new Date();
            const pad = (n) => n.toString().padStart(2, '0');
            const formattedDate = `${pad(now.getMonth() + 1)}/${pad(now.getDate())}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

            const normalizedEmail = newStaffEmail.trim().toLowerCase();
            const pendingRef = doc(db, "pendingPersonnel", normalizedEmail);

            await runTransaction(db, async (transaction) => {
                const existing = await transaction.get(pendingRef);
                if (existing.exists()) {
                    throw new Error("DUPLICATE_PENDING_PERSONNEL");
                }

                transaction.set(pendingRef, {
                    email: newStaffEmail.trim(),
                    password: newStaffPassword,
                    type: newStaffType,
                    createdAt: formattedDate,
                    status: "pending"
                });
            });
            
            // Set success modal data and show it
            setSuccessModalData({
                staffType: newStaffType,
                email: newStaffEmail,
                password: newStaffPassword
            });
            setShowSuccessModal(true);
            
            // Reset form
            setShowAddModal(false);
            setNewStaffType("ClinicStaff");
            setNewStaffEmail("");
            setNewStaffPassword("");
            setEmailError(""); // Clear any email errors
            setShowValidationErrors(false); // Reset validation state
            setEmailValidationError(""); // Clear validation errors
            setPasswordValidationError(""); // Clear validation errors
        } catch (error) {
            if (error?.message === "DUPLICATE_PENDING_PERSONNEL") {
                setEmailError("This email/username is already in the pending personnel list.");
            } else {
                alert("Failed to add personnel: " + error.message);
            }
        }
        setIsSubmitting(false);
    };


  // UseEffects
  useEffect(() => {
    const details = fetchUserDetails(setUserDetails);
    const redirect = redirectIfNotLoggedIn(() => {}, navigate);
    const notify = notifyFirstUser();
    return () => {
      details && details();
      redirect && redirect();
      notify && notify();
    };
  }, [navigate]);

  const boxStylesContent = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#FDF9F3",
    // width: "100%"
  }

    const accordionStylesFaqs = {
    // margin: "10px auto",
                                border: "1px solid rgba(89, 84, 74, .20)",
                                
                          boxShadow: '0 1px 10px rgba(0,0,0,0.20)',
                                display: "flex",
                                width: "100%",
                                flexDirection: "column",
    backgroundColor: "#fefefe",
    transition:"0.2s ease-in-out",
                                    borderRadius: "16px",
    "&:hover": {
        backgroundColor: "#EAE6DA",
    },
    "&.MuiAccordion-root": {
        borderRadius: "16px",
    },
    
  }

    return (
        <div>

      {/* All Content */}
      <Box
        sx={boxStylesContent}>

        {/* Sidebar */}
        <Box
          display={{
            xs: "none",
            sm: "none",
            md: "block",
            lg: "block",
            xl: "block",
          }}>
        <Sidebar
          fName={userDetails?.firstName}
          lName={userDetails?.lastName}
          email={userDetails?.email}
        />
        </Box>

          <Box
            sx={{
              display: {
                xs: "block",
                sm: "block",
                md: "none",
                lg: "none",
                xl: "none",
              }
            }}
          >
          <Fab
            label="Add Personnel"
            place="top-end"
            destination="none"
            src={require("../../../assets/icons/add.png")}
            open={false}
            onClick={() => setShowAddModal(true)}/>
          </Box>

        {/*Grids */}
        <Grid
          container
          // rowSpacing={2}
          // columnSpacing={2}
          sx={{
            width: "100%",
            height: "100vh",
            // backgroundColor: "blue",
            "& .MuiGrid-item": {
              display: "flex",
              justifyContent: "center",
              // backgroundColor: "blue",
            },
          }}>

            {/* Main Content */}
            <Grid 
              size={{ 
                xs: 12, 
                sm: 12, 
                md: 12, 
                lg: 12, 
                xl: 12 
              }} 
              item
            >
                {/* Main Content */}
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#FDF9F3",
                  // backgroundColor: "#f8f5ef",
                }}>

                    <Stack
                        direction="column">

                    <Header
                        count={2}
                        page="Personnel Management"
                        setIsModalOpen={() => setShowAddModal(true)}/>

                  {/* Widgets Container*/}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      // boxShadow: "-1px 5px 10px rgba(0,0,0,0.25)",
                      height: "calc(100vh - 110px)", // 👈 fills height below the header (adjust 80px if header height changes)
                      overflow: "auto",
                      mt: "0px",
                      // padding: "0px"
                    }}
                  >

                    {/* Top Widgets */}
                    <Grid
                      container
                      spacing={2}
                      sx={{
                      width: "100%",
                      padding: "20px",
                      paddingBottom: "0",
                        // backgroundColor: "blue",
                        "& .MuiGrid-item": {
                          display: "flex",
                          justifyContent: "center",
                          // backgroundColor: "blue",
                        },
                      }}>


                      {/* Total Staff*/}
                      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }} item>
                        <Box border="1px solid #0000010ff" backgroundColor="#fefefe"
                        sx={{
                          // padding: "5px 0",
                          borderRadius: "16px",
                          border: "1px solid rgba(89, 84, 74, .20)",
                        }}>

                          <CardContent
                            sx={{
                              padding: "20px"
                            }}>

                              <Stack 
                                direction="row"
                                sx={{
                                  width: "100%",
                                  alignContent: "center",
                                  alignItems: "center",
                                  // backgroundColor: "mediumaquamarine",
                                  textAlign: "center",
                                  justifyContent: "space-between",
                                  justifyItems: "center"
                              }}>

                                <Text
                                  text="Total Staff"
                                  variant="h3Header"/>

                                <Icon
                                    src={require("../../../assets/icons/patients.png")}
                                    size="small"/>
                                                
                              </Stack>
                              
                                <Typography
                                  variant="h2"
                                  sx={{
                                    padding: "15px 10px",
                                    fontSize: "24px",
                                    // backgroundColor: "aqua",
                                  }}>{clinicStaff.length + dentistOwner.length + associateDentist.length}
                                </Typography>
                              

                          </CardContent>

                        </Box>
                      </Grid>

                      {/* Total Dentists */}
                      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }} item>
                        <Box border="1px solid #0000010ff" backgroundColor="#fefefe"
                        sx={{
                          // padding: "5px 0",
                          borderRadius: "16px",
                          border: "1px solid rgba(89, 84, 74, .20)",
                        }}>

                          <CardContent
                            sx={{
                              padding: "20px"
                            }}>

                              <Stack 
                                direction="row"
                                sx={{
                                  width: "100%",
                                  alignContent: "center",
                                  alignItems: "center",
                                  // backgroundColor: "mediumaquamarine",
                                  textAlign: "center",
                                  justifyContent: "space-between",
                                  justifyItems: "center"
                              }}>

                                <Text
                                  text="Total Dentists"
                                  variant="h3Header"/>

                                <Icon
                                    src={require("../../../assets/icons/patients.png")}
                                    size="small"/>
                                                
                              </Stack>
                              
                                <Typography
                                  variant="h2"
                                  sx={{
                                    padding: "15px 10px",
                                    fontSize: "24px",
                                    // backgroundColor: "aqua",
                                  }}>{dentistOwner.length + associateDentist.length}
                                </Typography>
                              

                          </CardContent>

                        </Box>
                      </Grid>

                      </Grid>

                    {/* Buttons */}
                    <Grid
                      container
                      spacing={2}
                      sx={{
                      width: "100%",
                      padding: "20px",
                      paddingBottom: "0",
                        // backgroundColor: "blue",
                        "& .MuiGrid-item": {
                          display: "flex",
                          justifyContent: "center",
                          // backgroundColor: "blue",
                        },
                      }}>


                      {/* All Personnel */}
                      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} item>
                        <Box
                            sx={{
                                justifyContent: "space-between",
                                display: "flex",
                                flexDirection: "row"
                            }}
                        >
                            <div style={{ display: "flex", gap: 16, marginBottom: 10, alignItems: "center" }}>
                    {PERIOD_OPTIONS.map(opt => (
                      <ButtonComp
                        key={opt.key}
                        buttonId="analytics"
                        variant="contained"
                        noElevate={true}
                        noRipple={true}
                        label={opt.label}
                        color="default"
                        paddings="large"
                        background="default"
                        border="special"
                        font="default"
                        weight="default"
                        transform="default"
                        hoverTextColor="default"
                        hoverBackground="default"
                        selected={filter}
                        keys={opt.key}
                        hoverBorder="dark"
                        noRead={false}
                        radius="special"
                        type="default"
                        justify="default"
                        destination="none"
                        clickPass={() => setFilter(opt.key)}
                        open={true}/>
                    ))}
                    </div>
                    
                    
                                <Search
                                    handleSearch={handleSearch}
                                    value={searchTerm}/>

                        </Box>
                      </Grid>

                      </Grid>

                    {/* People Widgets */}
                    <Grid
                      container
                      spacing={2}
                      columnSpacing={3}
                      rowSpacing={2}
                      sx={{
                      width: "100%",
                      padding: "20px",
                      paddingBottom: "0",
                        "& .MuiGrid-item": {
                          display: "flex",
                          justifyContent: "center",
                        },
                      }}>
                    

                                {filteredDentistOwner.map(person => {
                            const isCurrentUser = person.id === currentUserId;
                            const displayName = `Dr. ${person.firstName} ${person.lastName}${isCurrentUser ? ' (You)' : ''}`;
                            // Use person.id as the lookup key (works for dentists with or without email)
                            const lookupKey = person.id;
                            const isEditing = editingPerson && editingPerson.userType === "DentistOwner" && editingPerson.id === person.id;
                            
                            return (
                        <Grid key={person.id} size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }} item sx={{ borderRadius: "50px"}}>
                            {/* <Box border="1px solid #0000010ff" backgroundColor="#fefefe"
                                sx={{
                                borderRadius: "16px",
                                border: "1px solid rgba(89, 84, 74, .20)",
                                display: "flex",
                                width: "100%",
                                flexDirection: "column"
                            }}> */}
                    <Accordion 
                      elevation={0}
                      expanded={expanded === `owner${person.id}`}
                      onChange={(event, isExpanded) => handleChange(isExpanded, `owner${person.id}`)}
                      sx={accordionStylesFaqs}>
                      <AccordionSummary
                        id={`owner${person.id}header`}
                        aria-controls={`owner${person.id}content`}
                        expandIcon={<ExpandMoreIcon/>}
                        >

                                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                            {person?.profilePictureUrl ? (
            <>
                <Avatar
                alt="Profile"
                sx={{
                    width: "60px",
                    height: "60px",
                    border:"3px solid #59544A",
                    backgroundColor: "black"
                }}
                src={person?.profilePictureUrl}
                />
            </>
            ) : (
            <>
                <Avatar
                alt="Profile"
                sx={{
                    width: "60px",
                    height: "60px",
                    border:"3px solid #59544A",
                    backgroundColor: "#2C251E"
                }}>
                    <Icon
                    src={require("../../../assets/user.png")}
                    size="small"/>
                </Avatar>
            </>
            )}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <Text
                                                    text={displayName}
                                                    variant="manageName"
                                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}/>
                                                <Text
                                                    text="Dentist Owner"
                                                    variant="manageType"/>
                                                <div style={{ color: '#888', fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                    {isEditing ? (
                                                        <>
                                                            <input
                                                                type="text"
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                style={{
                                                                    border: '1px solid #C7A76C',
                                                                    borderRadius: 4,
                                                                    padding: '2px 6px',
                                                                    fontSize: 13,
                                                                    width: '120px'
                                                                }}
                                                                placeholder="Enter specialty"
                                                            />
                                                            <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                                                                <button 
                                                                    onClick={handleSaveSpecialty}
                                                                    style={{
                                                                        background: '#4CAF50',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: 3,
                                                                        padding: '2px 6px',
                                                                        fontSize: 11,
                                                                        cursor: 'pointer',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    Save
                                                                </button>
                                                                <button 
                                                                    onClick={handleCancelEdit}
                                                                    style={{
                                                                        background: '#f44336',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: 3,
                                                                        padding: '2px 6px',
                                                                        fontSize: 11,
                                                                        cursor: 'pointer',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                    <Box>
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                            sx={{
                                                                alignItems: "center",
                                                            }}>
                                                            
                                                <Text
                                                    text={person.specialty || 'General Dentistry'}
                                                    variant="manageDoc"/>
                                                    <Tooltip
                                                        title="Edit"
                                                        placement="top"
                                                        arrow
                                                    >
                                                <Box
                                            onClick={() => handleEditSpecialty("DentistOwner", person.id, person.specialty)}
                                            sx={{
                                            borderRadius: "50px",
                                            height: "25px",
                                            width: "25px",
                                            cursor: "pointer",
                                            ":hover": {
                                                backgroundColor: "rgba(46, 45, 45, 0.2)"
                                            }
                                            }}>
                                            <Icon
                                            src={require("../../../assets/icons/edit.png")}
                                            size="extra-small"/>
                                        </Box>
                                        </Tooltip>
                                        </Stack>
                                                        </Box>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                      </AccordionSummary>

                      <AccordionDetails sx={{ py: "0", pb: "16px"}}>

                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            sx={{
                                                mt: "0"
                                            }}>
                                            {person.email && (
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{
                                                        alignItems: "center"
                                                    }}>
                                                <Icon
                                                    src={require("../../../assets/icons/email.png")}
                                                    size="small-no-margin"/>
                                                <Text
                                                    text={person.email}
                                                    variant="manageDoc"
                                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}/>
                                                </Stack>
                                            )}
                                        <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{
                                                    alignItems: "center"
                                                }}>
                                            <Icon
                                                src={require("../../../assets/icons/phone_thick.png")}
                                                size="small-no-margin"/>
                                            <Text
                                                text={person.contactNumber || 'N/A'}
                                                variant="manageDoc"
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}/>
                                        </Stack>
                                                    
                                        </Stack>

                                        <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{
                                                    alignItems: "center",
                                                    mt: "10px"
                                                }}>
                                            <Icon
                                                src={require("../../../assets/icons/patients_handled.png")}
                                                size="small-no-margin"/>
                                            <Stack
                                                direction="row"
                                                spacing="3px">
                                            <Text
                                                text={patientCounts[lookupKey] || 0}
                                                variant="manageDoc"/>
                                            <Text
                                                text="patients"
                                                variant="manageDoc"/>
                                            </Stack>
                                        </Stack>

                      </AccordionDetails>
                    </Accordion>


                            {/* </Box> */}
                        </Grid>
                            );
                        })}
                                                      


                               {filteredAssociateDentist.map(person => {
                            const displayName = `Dr. ${person.firstName} ${person.lastName}`;
                            // Use person.id as the lookup key (works for dentists with or without email)
                            const lookupKey = person.id;
                            const isEditing = editingPerson && editingPerson.userType === "AssociateDentist" && editingPerson.id === person.id;
                            
                            return (
                      <Grid  key={person.id}  size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }} item>
                    <Accordion 
                      elevation={0}
                      expanded={expanded === `assoc${person.id}`}
                      onChange={(event, isExpanded) => handleChange(isExpanded, `assoc${person.id}`)}
                      sx={accordionStylesFaqs}>
                      <AccordionSummary
                        id={`assoc${person.id}header`}
                        aria-controls={`assoc${person.id}content`}
                        expandIcon={<ExpandMoreIcon/>}
                        >

                          <div style={{ display: 'flex', gap: 16}}>
                                        {person?.profilePictureUrl ? (
          <>
            <Avatar
              alt="Profile"
              sx={{
                width: "60px",
                height: "60px",
                border:"3px solid #59544A",
                backgroundColor: "black"
              }}
              src={person?.profilePictureUrl}
            />
          </>
        ) : (
          <>
            <Avatar
              alt="Profile"
              sx={{
                width: "60px",
                height: "60px",
                border:"3px solid #59544A",
                backgroundColor: "#2C251E"
              }}>
                <Icon
                  src={require("../../../assets/user.png")}
                  size="small"/>
            </Avatar>
          </>
        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Text
                                                text={displayName}
                                                variant="manageName"
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}/>
                                            <Text
                                                text="Associate Dentist"
                                                variant="manageType"/>
                                            <div style={{ color: '#888', fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                {isEditing ? (
                                                    <>
                                                        <input
                                                            type="text"
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            style={{
                                                                border: '1px solid #C7A76C',
                                                                borderRadius: 4,
                                                                padding: '2px 6px',
                                                                fontSize: 13,
                                                                width: '120px'
                                                            }}
                                                            placeholder="Enter specialty"
                                                        />
                                                        <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                                                            <button 
                                                                onClick={handleSaveSpecialty}
                                                                style={{
                                                                    background: '#4CAF50',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: 3,
                                                                    padding: '2px 6px',
                                                                    fontSize: 11,
                                                                    cursor: 'pointer',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                Save
                                                            </button>
                                                            <button 
                                                                onClick={handleCancelEdit}
                                                                style={{
                                                                    background: '#f44336',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: 3,
                                                                    padding: '2px 6px',
                                                                    fontSize: 11,
                                                                    cursor: 'pointer',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <Box>
                                                            <Stack
                                                                direction="row"
                                                                spacing={1}
                                                        sx={{
                                                            alignItems: "center",
                                                        }}>
                                                        
                                            <Text
                                                text={person.specialty || 'General Dentistry'}
                                                variant="manageDoc"/>
                                                <Tooltip
                                                    title="Edit"
                                                    placement="top"
                                                    arrow
                                                >
                                             <Box
                                        onClick={() => handleEditSpecialty("AssociateDentist", person.id, person.specialty)}
                                        sx={{
                                          borderRadius: "50px",
                                          height: "25px",
                                          width: "25px",
                                          cursor: "pointer",
                                          ":hover": {
                                            backgroundColor: "rgba(46, 45, 45, 0.2)"
                                          }
                                        }}>
                                        <Icon
                                          src={require("../../../assets/icons/edit.png")}
                                          size="extra-small"/>
                                      </Box>
                                      </Tooltip>
                                      </Stack>
                                                    </Box>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                      </AccordionSummary>

                      <AccordionDetails  sx={{ py: "0", pb: "16px"}}>

                        <Stack
                            direction="column"
                            spacing={1}
                            justifyContent="end"
                            justifyItems="end"
                            textAlign="end"
                            alignContent="end"
                            alignItems="end"
                        >
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        sx={{
                                            mt: "0px",
                                            width: "100%",
                                        }}>
                                         {person.email && (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                                alignItems: "center"
                                            }}>
                                        <Icon
                                            src={require("../../../assets/icons/email.png")}
                                            size="small-no-margin"/>
                                        <Text
                                            text={person.email}
                                            variant="manageDoc"
                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}/>
                                        </Stack>
                                         )}
                                    <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                                alignItems: "center"
                                            }}>
                                        <Icon
                                            src={require("../../../assets/icons/phone_thick.png")}
                                            size="small-no-margin"/>
                                        <Text
                                            text={person.contactNumber || 'N/A'}
                                            variant="manageDoc"
                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}/>
                                    </Stack>
                                                
                                    </Stack>

                                    <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                            width: "100%",
                                                alignItems: "center",
                                                mt: "10px"
                                            }}>
                                        <Icon
                                            src={require("../../../assets/icons/patients_handled.png")}
                                            size="small-no-margin"/>
                                        <Stack
                                            direction="row"
                                            spacing="3px">
                                        <Text
                                            text={patientCounts[lookupKey] || 0}
                                            variant="manageDoc"/>
                                        <Text
                                            text="patients"
                                            variant="manageDoc"/>
                                        </Stack>
                                    </Stack>
<Box
                                        onClick={() =>
                                                handleDelete({
                                                    userType: person.userType || person.role || "AssociateDentist",
                                                    id: person.id,
                                                    firstName: person.firstName,
                                                    lastName: person.lastName,
                                                    gender: person.gender
                                                })
                                            }
                                        sx={{
                                backgroundColor: "#9F4223",
                                          borderRadius: "50px",
                                          width: "fit-content",
                                          p: "10px 20px 12px 20px",
                                          ":hover": {
                                            backgroundColor: "#80361e",
                                          },
                                          cursor: "pointer",
                                        }}>
                                                <Text
                                                    text="Remove"
                                                    variant="removePersonnelHover"/>
                                      </Box>
                                    </Stack>

                      </AccordionDetails>
                    </Accordion>
                      </Grid>
                            );
                        })}
                                                      

                            {filteredClinicStaff.map(person => {
                            const isEditing = editingPerson && editingPerson.userType === "ClinicStaff" && editingPerson.id === person.id;
                            
                            return (
                      <Grid  key={person.id} size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }} item>
 <Accordion 
                      elevation={0}
                      expanded={expanded === `staff${person.id}`}
                      onChange={(event, isExpanded) => handleChange(isExpanded, `staff${person.id}`)}
                      sx={accordionStylesFaqs}>
                      <AccordionSummary
                        id={`staff${person.id}header`}
                        aria-controls={`staff${person.id}content`}
                        expandIcon={<ExpandMoreIcon/>}
                        >
<div style={{ display: 'flex', gap: 16 }}>
                                        {person?.profilePictureUrl ? (
          <>
            <Avatar
              alt="Profile"
              sx={{
                width: "60px",
                height: "60px",
                border:"3px solid #59544A",
                backgroundColor: "black"
              }}
              src={person?.profilePictureUrl}
            />
          </>
        ) : (
          <>
            <Avatar
              alt="Profile"
              sx={{
                width: "60px",
                height: "60px",
                border:"3px solid #59544A",
                backgroundColor: "#2C251E"
              }}>
                <Icon
                  src={require("../../../assets/user.png")}
                  size="small"/>
            </Avatar>
          </>
        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Text
                                                text={`${person.firstName} ${person.lastName}`}
                                                variant="manageName"
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}/>
                                            <Text
                                                text="Clinic Staff"
                                                variant="manageType"/>
                                        </div>
                                    </div>
                   
                      </AccordionSummary>

                      <AccordionDetails sx={{ py: "0", pb: "16px"}}>
                        <Stack
                            direction="column"
                            spacing={1}
                            justifyContent="end"
                            justifyItems="end"
                            textAlign="end"
                            alignContent="end"
                            alignItems="end"
                        >
 <Stack
                                        direction="row"
                                        spacing={2}
                                        sx={{
                                            // alignItems: "start",
                                            // justifyContent: "start",
                                            // justifyItems: "start",
                                            // alignContent: "start",
                                            // textAlign: "start",
                                            // backgroundColor: "blue",
                                            width: "100%",
                                            mt: "0px"
                                        }}>
                                        {person.email && (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                                alignItems: "center"
                                            }}>
                                        <Icon
                                            src={require("../../../assets/icons/email.png")}
                                            size="small-no-margin"/>
                                        <Text
                                            text={person.email}
                                            variant="manageDoc"
                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}/>
                                        </Stack>
                                        )}
                                    <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                                alignItems: "center"
                                            }}>
                                        <Icon
                                            src={require("../../../assets/icons/phone_thick.png")}
                                            size="small-no-margin"/>
                                        <Text
                                            text={person.contactNumber || 'N/A'}
                                            variant="manageDoc"
                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}/>
                                    </Stack>
                                                
                                    </Stack>
                                    
                                      <Box
                                        onClick={() =>
                                                handleDelete({
                                                    userType: person.userType || person.role || "ClinicStaff",
                                                    id: person.id,
                                                    firstName: person.firstName,
                                                    lastName: person.lastName,
                                                    gender: person.gender
                                                })
                                            }
                                        sx={{
                                backgroundColor: "#9F4223",
                                          borderRadius: "50px",
                                          width: "fit-content",
                                          p: "10px 20px 12px 20px",
                                          ":hover": {
                                            backgroundColor: "#80361e",
                                          },
                                          cursor: "pointer",
                                        }}>
                                                <Text
                                                    text="Remove"
                                                    variant="removePersonnelHover"/>
                                      </Box>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                      </Grid>
                            );
                        })}
                                                      



                      </Grid>
            </Box>
            </Stack>

            <Snackbar
                open={showSpecialtySnackbar}
                autoHideDuration={3000}
                onClose={handleSpecialtySnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={handleSpecialtySnackbarClose}
                    severity={specialtySnackbarSeverity}
                    variant="filled"
                    icon={false}
                    sx={{
                        backgroundColor: specialtySnackbarSeverity === "success" ? "#558363" : "rgb(159, 66, 35)",
                        color: "#fff",
                        fontWeight: 600,
                        letterSpacing: 0.2,
                        borderRadius: "12px",
                        padding: "8px 16px",
                        minWidth: "auto",
                        width: "auto",
                        boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center"
                    }}
                >
                    {specialtySnackbarMessage}
                </Alert>
            </Snackbar>

            </Box>
            
            </Grid>
            </Grid>
            </Box>

                {/* Add Staff/Personnel Modal */}
                {showAddModal && (
                    <Dialog
                        open={showAddModal}
                        onClose={handleModalClose}
                        maxWidth="sm"
                        fullWidth={false}
                        sx={{
                            "& .MuiDialog-paper": {
                                borderRadius: "16px",
                                overflow: "hidden",
                                bgcolor: "#FDF9F3",
                                boxShadow: "0 18px 60px rgba(44,37,30,0.14)",
                                width: "500px",
                                maxWidth: "90vw",
                            },
                            // Override MUI default that removes top padding from DialogContent when following DialogTitle
                            "& .MuiDialogTitle-root + .MuiDialogContent-root": {
                                paddingTop: "24px",
                            },
                        }}
                        PaperProps={{
                            sx: {
                                borderRadius: "16px",
                                overflow: "hidden",
                                bgcolor: "#FDF9F3",
                                boxShadow: "0 18px 60px rgba(44,37,30,0.14)",
                                m: 0,
                            },
                        }}
                        aria-labelledby="personnel-dialog-title"
                    >
                        <DialogTitle
                            id="personnel-dialog-title"
                            sx={{
                                bgcolor: "#FDF9F3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                px: 3,
                                py: 2,
                                borderRadius: "16px 16px 0 0",
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }}>
                                Add Personnel
                            </Typography>

                            <IconButton
                                onClick={handleModalClose}
                                size="small"
                                sx={{
                                    position: "absolute",
                                    right: 16,
                                    width: 28,
                                    height: 28,
                                    border: "1px solid rgba(44,37,30,0.06)",
                                    bgcolor: "#fff",
                                    color: "#2E2D2D",
                                    fontSize: "16px",
                                    "&:hover": { bgcolor: "#C5A572", color: "#FEFEFE" },
                                }}
                                aria-label="close"
                            >
                                ✕
                            </IconButton>
                        </DialogTitle>

                        <DialogContent
                            sx={{
                                pt: "24px",
                                px: 3,
                                pb: 3,
                                bgcolor: "#FEFEFE",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "stretch",
                                justifyContent: "flex-start",
                                gap: 0,
                                overflowX: "hidden",
                                overflowY: "auto",
                                "&.MuiDialogContent-root": {
                                    paddingTop: "24px",
                                },
                            }}
                        >
                            <Box
                                component="form"
                                onSubmit={handleAddPersonnel}
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                <Box>
                                    <label style={{ fontWeight: 500, marginBottom: 8, display: "block", color: "#000000" }}>
                                        Staff Type
                                    </label>
                                    <select 
                                        value={newStaffType} 
                                        onChange={e => setNewStaffType(e.target.value)} 
                                        style={{ 
                                            padding: "10px", 
                                            borderRadius: "6px", 
                                            border: '1px solid #C7A76C', 
                                            fontSize: 16,
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            backgroundColor: '#fff'
                                        }}
                                    >
                                        <option value="ClinicStaff">Clinic Staff</option>
                                        <option value="AssociateDentist">Associate Dentist</option>
                                        <option value="DentistOwner">Dentist Owner</option>
                                    </select>
                                </Box>

                                <Box>
                                    <label style={{ fontWeight: 500, marginBottom: 8, display: "block", color: "#000000" }}>
                                        Email or Username <span style={{ color: '#f44336' }}>*</span>
                                    </label>
                                    <Box sx={{ position: "relative" }}>
                                        <input 
                                            type="text" 
                                            value={newStaffEmail} 
                                            onChange={handleEmailChange} 
                                            style={{ 
                                                padding: "10px", 
                                                borderRadius: "6px", 
                                                border: (showValidationErrors && emailValidationError) || emailError ? '1px solid #f44336' : '1px solid #C7A76C', 
                                                fontSize: 16,
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                backgroundColor: '#fff'
                                            }} 
                                            placeholder="Enter email or username"
                                        />
                                        {isCheckingEmail && (
                                            <Box sx={{
                                                position: "absolute",
                                                right: 10,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                fontSize: 14,
                                                color: "#666"
                                            }}>
                                                Checking...
                                            </Box>
                                        )}
                                    </Box>
                                    {showValidationErrors && emailValidationError && (
                                        <Typography variant="caption" sx={{ 
                                            color: '#f44336', 
                                            fontSize: '0.75rem',
                                            mt: 0.5,
                                            display: 'block'
                                        }}>
                                            {emailValidationError}
                                        </Typography>
                                    )}
                                    {emailError && (
                                        <Typography variant="caption" sx={{ 
                                            color: '#f44336', 
                                            fontSize: '0.75rem',
                                            mt: 0.5,
                                            display: 'block'
                                        }}>
                                            {emailError}
                                        </Typography>
                                    )}
                                </Box>

                                <Box>
                                    <label style={{ fontWeight: 500, marginBottom: 8, display: "block", color: "#000000" }}>
                                        Password <span style={{ color: '#f44336' }}>*</span>
                                    </label>
                                    <div style={{ position: "relative" }}>
                                        <input 
                                            type={showNewStaffPassword ? "text" : "password"} 
                                            value={newStaffPassword} 
                                            onChange={e => {
                                                setNewStaffPassword(e.target.value);
                                                // Clear validation errors when user starts typing
                                                if (showValidationErrors && passwordValidationError) {
                                                    setPasswordValidationError("");
                                                }
                                            }} 
                                            style={{ 
                                                padding: "10px 40px 10px 10px", 
                                                borderRadius: "6px", 
                                                border: (showValidationErrors && passwordValidationError) ? '1px solid #f44336' : '1px solid #C7A76C', 
                                                fontSize: 16,
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                backgroundColor: '#fff'
                                            }} 
                                            placeholder="Enter password"
                                        />
                                        {newStaffPassword && (
                                            <button
                                                type="button"
                                                onClick={() => setShowNewStaffPassword(!showNewStaffPassword)}
                                                aria-label={showNewStaffPassword ? "Hide password" : "Show password"}
                                                style={{
                                                    position: "absolute",
                                                    right: "8px",
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    background: "transparent",
                                                    border: "none",
                                                    cursor: "pointer",
                                                    padding: 0,
                                                    color: "#2C251E",
                                                }}
                                            >
                                                {showNewStaffPassword ? <Visibility /> : <VisibilityOff />}
                                            </button>
                                        )}
                                    </div>
                                    {showValidationErrors && passwordValidationError && (
                                        <Typography variant="caption" sx={{ 
                                            color: '#f44336', 
                                            fontSize: '0.75rem',
                                            mt: 0.5,
                                            display: 'block'
                                        }}>
                                            {passwordValidationError}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </DialogContent>

                        <DialogActions sx={{ px: 3, pb: 3, pt: 0, bgcolor: "#FEFEFE", justifyContent: "center", borderRadius: "0 0 16px 16px" }}>
                            <Button
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleAddPersonnel(e);
                                }}
                                variant="contained"
                                disabled={isSubmitting || isCheckingEmail || emailError}
                                sx={{
                                    backgroundColor: (isSubmitting || isCheckingEmail || emailError) ? '#ccc' : "#C5A572",
                                    color: "#FEFEFE",
                                    textTransform: "none",
                                    width: "100%",
                                    py: 1,
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    "&:hover": { backgroundColor: (isSubmitting || isCheckingEmail || emailError) ? '#ccc' : "#A68B6E" },
                                    "&:disabled": { backgroundColor: '#ccc', color: '#fff' }
                                }}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
                {/* Success Modal */}
                {showSuccessModal && (
                    <div 
                        style={{
                            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
                        }}
                        onClick={(e) => {
                            // Prevent closing when clicking on backdrop
                            e.stopPropagation();
                        }}
                    >
                        <div 
                            style={{ background: "#fff", borderRadius: 16, padding: 36, minWidth: 500, maxWidth: 600, position: "relative" }}
                            onClick={(e) => {
                                // Prevent closing when clicking inside modal
                                e.stopPropagation();
                            }}
                        >
                            <div style={{ textAlign: "center", marginBottom: 24 }}>
                                <p style={{ 
                                    color: "#666", 
                                    fontSize: 16, 
                                    lineHeight: 1.5,
                                    marginBottom: 24
                                }}>
                                    One-time sign-in credentials for the <strong>{successModalData.staffType === "DentistOwner" ? "dentist owner" : 
                                    successModalData.staffType === "AssociateDentist" ? "associate dentist" : "clinic staff"}</strong> have been generated. 
                                    Please share these credentials with the user so they can sign in and complete their account registration.
                                </p>
                            </div>
                            <div style={{ 
                                background: "#f8f9fa", 
                                borderRadius: 12, 
                                padding: 20, 
                                marginBottom: 24,
                                border: "1px solid #e9ecef"
                            }}>
                                <h3 style={{ marginBottom: 16, color: "#333", fontSize: 18 }}>Login Credentials:</h3>
                                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ fontWeight: 500, color: "#666" }}>Email/Mobile:</span>
                                        <span style={{ 
                                            background: "#fff", 
                                            padding: "8px 12px", 
                                            borderRadius: 6, 
                                            border: "1px solid #ddd",
                                            fontFamily: "monospace",
                                            fontSize: 14
                                        }}>
                                            {successModalData.email}
                                        </span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ fontWeight: 500, color: "#666" }}>Password:</span>
                                        <span style={{ 
                                            background: "#fff", 
                                            padding: "8px 12px", 
                                            borderRadius: 6, 
                                            border: "1px solid #ddd",
                                            fontFamily: "monospace",
                                            fontSize: 14
                                        }}>
                                            {successModalData.password}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div style={{ textAlign: "center" }}>
                                <button 
                                    onClick={() => setShowSuccessModal(false)}
                                    style={{ 
                                        background: '#C5A572', 
                                        color: 'white', 
                                        border: 'none', 
                                        borderRadius: 20, 
                                        padding: '12px 32px', 
                                        fontWeight: 600, 
                                        fontSize: 16, 
                                        cursor: 'pointer',
                                        minWidth: 120
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#A68B6E';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#C5A572';
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* Delete Confirmation Modal */}
                <Dialog
                    open={showDeleteModal}
                    disableEscapeKeyDown
                    onClose={(_, reason) => {
                        // Allow closing on backdrop click; still block while busy
                        if (isRemovingPersonnel || isCancellingDelete) {
                            return;
                        }
                        cancelDelete();
                    }}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            backgroundColor: "#FFFFFF",
                            paddingBottom: 2,
                        },
                    }}
                    BackdropProps={{
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            textAlign: "center",
                            fontWeight: 700,
                            color: "#C94D34",
                            fontSize: "22px",
                            padding: "28px 32px 12px 32px",
                            position: "relative",
                        }}
                    >
                        {removalTitle}
                        <IconButton
                            onClick={cancelDelete}
                            disabled={isCancellingDelete || isRemovingPersonnel}
                            sx={{
                                position: "absolute",
                                right: 16,
                                top: 16,
                                color: isCancellingDelete ? "#B0A99F" : "#6D6257",
                                "&:hover": {
                                    backgroundColor: (isCancellingDelete || isRemovingPersonnel) ? "transparent" : "rgba(109, 98, 87, 0.08)",
                                },
                            }}
                        >
                            {isCancellingDelete ? (
                                <CircularProgress size={18} sx={{ color: "#B0A99F" }} />
                            ) : (
                                <Close fontSize="small" />
                            )}
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ padding: "0 32px 8px 32px" }}>
                        <Stack spacing={2} alignItems="center">
                            <Typography
                                sx={{
                                    color: "#2C251E",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    textAlign: "center",
                                }}
                            >
                                {removalMessage}
                            </Typography>
                            <Typography
                                sx={{
                                    color: "rgba(44, 37, 30, 0.6)",
                                    fontSize: "14px",
                                    textAlign: "center",
                                }}
                            >
                                This action cannot be undone. All relevant personnel will be notified.
                            </Typography>
                        </Stack>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            padding: "12px 32px 28px 32px",
                            justifyContent: "center",
                            gap: 2,
                        }}
                    >
                        <Button
                            onClick={cancelDelete}
                            disabled={isCancellingDelete || isRemovingPersonnel}
                            variant="outlined"
                            sx={{
                                color: isCancellingDelete ? "#B0A99F" : "#6D6257",
                                borderColor: isCancellingDelete ? "#D3CBC1" : "#6D6257",
                                padding: "10px 22px",
                                borderRadius: 20,
                                fontSize: "15px",
                                fontWeight: 600,
                                minWidth: "120px",
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: isCancellingDelete || isRemovingPersonnel ? "transparent" : "rgba(109, 98, 87, 0.08)",
                                    borderColor: isCancellingDelete ? "#D3CBC1" : "#6D6257",
                                },
                                "&:disabled": {
                                    borderColor: "#D3CBC1",
                                    color: "#B0A99F",
                                },
                            }}
                        >
                            {isCancellingDelete ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: "#B0A99F" }} />
                                    <span>Cancelling...</span>
                                </Box>
                            ) : (
                                "No, Cancel"
                            )}
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            disabled={isRemovingPersonnel || isCancellingDelete}
                            variant="contained"
                            sx={{
                                backgroundColor: isRemovingPersonnel ? "#D3CBC1" : "#C94D34",
                                color: "#fff",
                                padding: "10px 22px",
                                borderRadius: 20,
                                fontSize: "15px",
                                fontWeight: 600,
                                minWidth: "120px",
                                textTransform: "none",
                                boxShadow: "none",
                                "&:hover": {
                                    backgroundColor: isRemovingPersonnel ? "#D3CBC1" : "#A93F28",
                                    boxShadow: "none",
                                },
                                "&:disabled": {
                                    backgroundColor: "#D3CBC1",
                                    color: "#F5F1EC",
                                },
                            }}
                        >
                            {isRemovingPersonnel ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                                    <span>Removing...</span>
                                </Box>
                            ) : (
                                "Yes, Remove"
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>

            <Snackbar
                open={showRemovalSnackbar}
                autoHideDuration={3000}
                onClose={handleRemovalSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={handleRemovalSnackbarClose}
                    severity="success"
                    variant="filled"
                    icon={false}
                    sx={{
                        backgroundColor: "#558363",
                        color: "#fff",
                        fontWeight: 600,
                        letterSpacing: 0.2,
                        borderRadius: "12px",
                        padding: "8px 16px",
                        minWidth: "auto",
                        width: "auto",
                        boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center"
                    }}
                >
                    {removalSnackbarMessage}
                </Alert>
            </Snackbar>

    <SplashScreen
      isOpen={showCustomAlert}
      image={alertImage}
      button={alertButton}
      buttonType={alertButtonType}
      destination={alertLink}
      onClose={() => setShowCustomAlert(false)}/>
        </div>
    );
};

export default ManagePersonnel;